import { useToastStore } from '@/store/ToastStore.js';

// Custom toast layer (Pinia-backed; the <ToastLayer /> mounted once in App.vue
// renders it). Public API: Success/Warning/Info(message, options?) and
// Error(error, fallback?, options?) — see ResolveError below for Error's
// specific contract.
//
// options: { title?, duration?, persistent? }
//   - title:      optional bold heading above the message
//   - duration:   override the default lifetime (ms)
//   - persistent: keep the toast until the user dismisses it (error use case)
//
// The store is resolved lazily inside each call — never at module load — so
// Pinia is guaranteed to be installed by the time a toast actually fires.
function push(type, message, options = {}) {
    const store = useToastStore();
    return store.Add({ type, message, ...options });
}

// Matches the CLAUDE.md catch-block convention app-wide — shown only when the
// failure never made it to the API (see ResolveError below), so there is no
// action-specific text and no request to reference.
const GENERIC_ERROR_MESSAGE = 'Ocurrió un error inesperado';

// RequestSender's HandleError stamps `status` on every error it throws for a
// real API response — controlled (4xx, business rule) or uncontrolled (500).
// A raw frontend exception (e.g. a null-property access in a component) never
// passes through it, so it never carries `status`/`error_code`/`unhandled` —
// that absence is what tells apart "this happened while calling the API" from
// "this never reached the API at all".
function IsApiError(error) {
    return error != null && typeof error === 'object'
        && (error.status !== undefined || error.error_code !== undefined || error.unhandled === true);
}

// `Error` takes the error itself (not a pre-built message string) and decides
// what to show:
//   - plain string             -> shown as-is (frontend-only validation toasts
//                                  that never had an error object behind them)
//   - NOT an API error          -> the app-wide generic message, always. There
//     (no status/error_code/       is no action-specific text worth trusting
//      unhandled at all)           here (e.g. a plain JS ReferenceError) and no
//                                  correlation id to reference.
//   - CONTROLLED API error      -> error.message (the API's own text), falling
//     (status set, no unhandled)   back to `fallback` if the API sent none.
//   - UNCONTROLLED API error    -> ALWAYS the caller's action-specific
//     (`unhandled`/`error_id`)     `fallback` (never the generic message) plus
//                                  a Ref/copy row built from `error_id`, kept
//                                  on screen until the user dismisses it.
function ResolveError(error, fallback) {
    if (typeof error === 'string') {
        return { message: error, errorId: null, persistent: false };
    }

    if (!IsApiError(error)) {
        // Non-API errors include real network failures (RethrowRequestError throws
        // a plain Error with no status), where the caller's action-specific
        // `fallback` is still the most useful text — keep it, generic only if none.
        return { message: fallback ?? GENERIC_ERROR_MESSAGE, errorId: null, persistent: false };
    }

    if (error.unhandled) {
        return { message: fallback ?? GENERIC_ERROR_MESSAGE, errorId: error.error_id ?? null, persistent: true };
    }

    return { message: error.message ?? fallback ?? GENERIC_ERROR_MESSAGE, errorId: null, persistent: false };
}

const ShowNotification = {
    Success: (message, options) => push('success', message, options),

    // `error`: string OR the error object caught from a service call.
    // `fallback`: message to use when a CONTROLLED error carries no message.
    Error: (error, fallback, options = {}) => {
        const resolved = ResolveError(error, fallback);
        return push('error', resolved.message, {
            ...options,
            errorId: resolved.errorId,
            persistent: options.persistent ?? resolved.persistent
        });
    },

    Warning: (message, options) => push('warning', message, options),
    Info: (message, options) => push('info', message, options),

    // Dismiss a specific toast by the id returned from a Success/Error/... call.
    Dismiss: (id) => useToastStore().Dismiss(id),
    Clear: () => useToastStore().Clear()
};

export default ShowNotification;
